import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import LoadingIndicator from 'components/LoadingIndicator';
import './InfiniteScroll.css';

interface InfiniteScrollProps<T> {
  data?: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  emptyListMessage?: string;
  onEndReached?: () => void;
}

export const InfiniteScrollInner = <T,>(
  {
    data,
    renderItem,
    isLoading,
    isLoadingMore,
    emptyListMessage,
    onEndReached,
  }: InfiniteScrollProps<T>,
  ref: React.Ref<HTMLDivElement>
) => {
  const localRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

  useEffect(() => {
    const el = localRef.current;
    if (!el || !onEndReached) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const scrollPercent = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercent >= 0.95) {
        onEndReached();
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [localRef, onEndReached]);

  return (
    <div ref={localRef} className="infinite-scroll">
      {isLoading ? (
        <div className="infinite-scroll-loading-indicator-container">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {data && data.length ? (
            data.map((item, index) => renderItem(item, index))
          ) : (
            <div className="infinite-scroll-empty-message-container">
              <div className="infinite-scroll-empty-message">
                {emptyListMessage}
              </div>
            </div>
          )}
          {isLoadingMore && (
            <div className="infinite-scroll-loading-more-indicator-container">
              <LoadingIndicator />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const InfiniteScroll = forwardRef(InfiniteScrollInner) as <T>(
  props: InfiniteScrollProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof InfiniteScrollInner>;
